import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM, type TCoreLayoutStyleProps, type TCoreTextStyleProps } from '@core/widgets';
import { sanitizeUrl } from '@libs/core';
import React from 'react';
import { TConsumerItem } from '../../types/data';
import { TextStyle } from '../../types/styles';
import styled from './carousel.module.css';
import { useCarouselPalette } from './useCarouselPalette';

const KEY_ITEM_LAYOUT = 'carouselItemLayout';
const KEY_ITEM_TEXT = 'carouselItemText';

const BASE_ITEM_LAYOUT: TCoreLayoutStyleProps = {
    orientation: ORIENTATION_ENUM.VERTICAL,
    borderColor: 'var(--purple_100)',
    borderRadius: 15,
    borderStroke: 2,
    height: 140,
    padding: { top: 4, right: 4, bottom: 4, left: 4 },
    margin: { top: 2, right: 8, bottom: 2, left: 8 },
};
const BASE_INNER_RADIUS = 12;
const BASE_GRADIENT = 'var(--gradient-blue_1)';
const BASE_ITEM_TEXT: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: 'var(--white_base)',
    maxLines: 2,
};

const toGradientCss = (gradient: unknown): string | undefined => {
    if (!gradient || typeof gradient !== 'object') return undefined;
    const stops = Object.keys(gradient as Record<string, string>)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => (gradient as Record<string, string>)[k])
        .filter(Boolean);
    if (stops.length >= 2) return `linear-gradient(180deg, ${stops.join(', ')})`;
    return stops[0];
};

type TCarouselSlideProps = {
    item: TConsumerItem;
    styleName?: string;
};

export const CarouselSlide = ({ item, styleName }: TCarouselSlideProps) => {
    const cardKey = item.style ?? styleName;
    const parentStyleNames = cardKey ? [cardKey] : undefined;

    const layout = useCarouselPalette(KEY_ITEM_LAYOUT, cardKey);
    const text = useCarouselPalette(KEY_ITEM_TEXT, cardKey);

    const themed = cardKey != null;

    if (!themed) {
        const token = item.background && styled[item.background] ? item.background : 'blue_1';
        const legacyBackground = item.imageSource ? `url(${sanitizeUrl(item.imageSource)})` : `var(--gradient-${token})`;
        return (
            <CoreLayoutView
                uid={`carousel-item-${item.value}`}
                styleName={KEY_ITEM_LAYOUT}
                styleProps={{
                    orientation: ORIENTATION_ENUM.VERTICAL,
                    lineX: 'start',
                    lineY: 'bottom',
                    backgroundColor: legacyBackground,
                    borderColor: 'var(--purple_100)',
                    borderStroke: 1,
                    borderRadius: 12,
                    height: 120,
                    padding: { top: 12, right: 12, bottom: 12, left: 12 },
                    margin: { top: 2, right: 8, bottom: 2, left: 8 },
                }}
            >
                <CoreTextView uid={`carousel-item-text-${item.value}`} text={item.text} styleProps={{ ...TextStyle.secondary, main: 'var(--white_base)' }} />
            </CoreLayoutView>
        );
    }

    const gradientCss = toGradientCss(layout.backgroundGradient);
    const solidColor = typeof layout.backgroundColor === 'string' ? layout.backgroundColor : undefined;
    const imageUrl = item.backgroundUrl || item.imageSource;
    const textMaxLines = typeof text.maxLines === 'number' ? text.maxLines : undefined;
    const textLineHeight = (text.fontStyle as { lineHeight?: number } | undefined)?.lineHeight;
    const textPadTop = (text.padding as { top?: number } | undefined)?.top ?? 0;
    const clampStyle: React.CSSProperties | undefined =
        typeof textMaxLines === 'number' && typeof textLineHeight === 'number'
            ? { maxHeight: `${textPadTop + textMaxLines * textLineHeight}px`, overflow: 'hidden' }
            : undefined;

    const fillBackground = imageUrl ? `url(${sanitizeUrl(imageUrl)}) center / cover no-repeat` : (gradientCss ?? solidColor ?? BASE_GRADIENT);

    const innerRadius =
        typeof layout.inputBorderRadius === 'number'
            ? layout.inputBorderRadius
            : typeof layout.borderRadius === 'number'
              ? layout.borderRadius
              : BASE_INNER_RADIUS;

    const hasFixedHeight = layout.height != null || BASE_ITEM_LAYOUT.height != null;

    const strokeNum = typeof layout.borderStroke === 'number' ? layout.borderStroke : ((BASE_ITEM_LAYOUT.borderStroke as number | undefined) ?? 0);
    const themePad = (layout.padding as { top?: number; right?: number; bottom?: number; left?: number } | undefined) ?? BASE_ITEM_LAYOUT.padding;
    const ringInset = (v?: number) => `${Math.max(0, (v ?? 0) - strokeNum)}px`;

    return (
        <CoreLayoutView
            uid={`carousel-item-${item.value}`}
            styleName={KEY_ITEM_LAYOUT}
            parentStyleNames={parentStyleNames}
            styleProps={BASE_ITEM_LAYOUT}
            style={{
                background: 'transparent',
                paddingTop: ringInset(themePad?.top),
                paddingRight: ringInset(themePad?.right),
                paddingBottom: ringInset(themePad?.bottom),
                paddingLeft: ringInset(themePad?.left),
            }}
        >
            <div
                data-test={`carousel-fill-${item.value}`}
                style={{
                    width: '100%',
                    flexGrow: hasFixedHeight ? 1 : 0,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    overflow: 'hidden',
                    borderRadius: `${innerRadius}px`,
                    background: fillBackground,
                }}
            >
                <div style={{ minHeight: 0, ...clampStyle }}>
                    <CoreTextView
                        uid={`carousel-item-text-${item.value}`}
                        text={item.text}
                        styleName={KEY_ITEM_TEXT}
                        parentStyleNames={parentStyleNames}
                        styleProps={BASE_ITEM_TEXT}
                    />
                </div>
            </div>
        </CoreLayoutView>
    );
};
